import type { ButtonProps, ElementNode } from "@multi-tenant-cms/builder/types";
import { hasContainerBackgroundLayers } from "@multi-tenant-cms/builder/ui/lib/containerBackground";
import { normalizeHeadingElementTag } from "@multi-tenant-cms/builder/ui/lib/textElementTag";
import type {
	ButtonElement,
	CustomCodeElement,
	Element,
	FaqElement,
	FormEmbedElement,
	HeadingElement,
	IconifyIconElement,
	LordiconIconElement,
	LucideAnimatedIconElement,
	NavigationMenuElement,
	SortableListElement,
	TextElement,
} from "@multi-tenant-cms/builder/ui/types";
import {
	applyButtonStateStyleProps,
	attachLegacyFlatStyles,
	type BuilderColumnsProps,
	type BuilderCustomCodeProps,
	type BuilderFormEmbedProps,
	type BuilderNavigationMenuProps,
	containerSemanticProps,
	toBuilderStyle,
	withDom,
} from "@multi-tenant-cms/builder/utils/builderNodeSerializationPrimitives";

/**
 * A small, synchronous replacement for `pageBuilderAdapter.ts`'s `toBuilderNode` /
 * `buildBuilderNode`, covering only the 17 element types the mega-menu / lightbox
 * palette allows (`MEGA_MENU_PRESET_TYPE_SET` ∪ `LIGHTBOX_PRESET_TYPE_SET` — the union
 * is mega-menu's own list, since lightbox's is a strict subset). Used exclusively by
 * `normalizeLiveElementForRuntime.ts`, so that the public runtime can render live-editor
 * element shapes without statically pulling the ~120KB admin editor file (the full
 * ~36-type dispatcher, code-editor helpers, header-payload builders) into the public
 * client bundle.
 *
 * This is not a rare fallback: mega-menu and lightbox content is persisted in *editor*
 * shape (flat `styles`, no tokenized `style`), unlike entry content which is serialized
 * on save. An audit of every reachable local region found 100% of lightbox roots in
 * editor shape, so this converter is the only path by which that content renders —
 * treat it as load-bearing, not defensive.
 *
 * Output must stay identical to the admin adapter's for the same input, which is why
 * the conversion primitives both use (`toBuilderStyle`, `withDom`,
 * `attachLegacyFlatStyles`, the `Builder*Props` shapes) live in one shared module,
 * `utils/builderNodeSerializationPrimitives.ts`, rather than being mirrored by hand.
 * Only the per-type `case` bodies below are specific to this file; if a case changes in
 * `pageBuilderAdapter.ts`, mirror that case here.
 */

// ---- the 17-type dispatcher ----

function buildNarrowBuilderNode(element: Element): ElementNode | null {
	switch (element.type) {
		case "heading": {
			const he = element as HeadingElement;
			return {
				id: element.id,
				type: "heading",
				props: {
					content: element.content,
					...(he.html ? { html: true } : {}),
					tag: normalizeHeadingElementTag(he.tag),
					...(he.textBinding && he.textBinding !== "static"
						? { textBinding: he.textBinding }
						: {}),
				},
				style: toBuilderStyle(element.styles),
			};
		}
		case "text": {
			const textEl = element as TextElement;
			return {
				id: element.id,
				type: "text",
				props: {
					content: element.content,
					html: textEl.html ?? false,
					tag: textEl.tag ?? "p",
					...(textEl.textBinding && textEl.textBinding !== "static"
						? { textBinding: textEl.textBinding }
						: {}),
				},
				style: toBuilderStyle(element.styles),
			};
		}
		case "button": {
			const be = element as ButtonElement;
			const props: ButtonProps = {
				label: element.content,
				href: element.link || "",
				openInNewTab: element.target === "_blank",
				variant: "solid",
			};
			applyButtonStateStyleProps(be, props);
			if (be.buttonIconKind === "lucide_animated") {
				props.buttonIconKind = "lucide_animated";
				const mode = be.iconLucideMode === "local" ? "local" : "library";
				props.iconLucideMode = mode;
				if (mode === "library") {
					const lid = be.iconLucideLibraryId?.trim();
					if (lid) {
						props.iconLucideLibraryId = lid;
					}
				} else {
					const loc = be.iconLucideLocalSvgMarkup?.trim();
					if (loc) {
						props.iconLucideLocalSvgMarkup = loc;
					}
				}
				const lucAria = be.iconLucideAriaLabel?.trim();
				if (lucAria) {
					props.iconLucideAriaLabel = lucAria;
				}
				const prim = be.iconLucidePrimaryColor?.trim();
				if (prim) {
					props.iconLucidePrimaryColor = prim;
				}
				const sec = be.iconLucideSecondaryColor?.trim();
				if (sec) {
					props.iconLucideSecondaryColor = sec;
				}
				const sw = be.iconLucideStrokeWidth?.trim();
				if (sw) {
					props.iconLucideStrokeWidth = sw;
				}
				if (be.iconLucidePlayOn === "click") {
					props.iconLucidePlayOn = "click";
				}
				const host = be.iconLucideAnimationHostDomId?.trim();
				if (host) {
					props.iconLucideAnimationHostDomId = host;
				}
				if (
					typeof be.iconLucideSizePx === "number" &&
					Number.isFinite(be.iconLucideSizePx) &&
					be.iconLucideSizePx > 0
				) {
					props.iconLucideSizePx = Math.round(be.iconLucideSizePx);
				}
			} else {
				const leftSvg = be.iconLeftSvgMarkup?.trim() ?? "";
				const leftId = be.iconLeftIconId?.trim() ?? "";
				if (leftSvg || leftId) {
					props.iconLeftIconId = leftId;
					props.iconLeftSvgMarkup = leftSvg;
					const leftAria = be.iconLeftAriaLabel?.trim();
					if (leftAria) {
						props.iconLeftAriaLabel = leftAria;
					}
				}
				const rightSvg = be.iconRightSvgMarkup?.trim() ?? "";
				const rightId = be.iconRightIconId?.trim() ?? "";
				if (rightSvg || rightId) {
					props.iconRightIconId = rightId;
					props.iconRightSvgMarkup = rightSvg;
					const rightAria = be.iconRightAriaLabel?.trim();
					if (rightAria) {
						props.iconRightAriaLabel = rightAria;
					}
				}
			}
			if (be.iconPlacement === "left" || be.iconPlacement === "right") {
				props.iconPlacement = be.iconPlacement;
			}
			return {
				id: element.id,
				type: "button",
				props,
				style: toBuilderStyle(element.styles),
			};
		}
		case "image": {
			const img =
				element as import("@multi-tenant-cms/builder/ui/types").ImageElement;
			const binding =
				img.imageBinding ??
				(img.useFeaturedImage === true ? "featured" : "static");
			return {
				id: element.id,
				type: "image",
				props: {
					src: img.src || "",
					...(img.mediaId ? { mediaId: img.mediaId } : {}),
					...(binding === "static" ? {} : { imageBinding: binding }),
					...(binding === "featured"
						? { useFeaturedImage: true as const }
						: {}),
					alt: img.alt || "",
					...(typeof img.intrinsicWidth === "number" &&
					Number.isFinite(img.intrinsicWidth) &&
					img.intrinsicWidth > 0
						? { intrinsicWidth: Math.round(img.intrinsicWidth) }
						: {}),
					...(typeof img.intrinsicHeight === "number" &&
					Number.isFinite(img.intrinsicHeight) &&
					img.intrinsicHeight > 0
						? { intrinsicHeight: Math.round(img.intrinsicHeight) }
						: {}),
					title: img.title ?? "",
					caption: img.caption ?? "",
					...(img.showCaption === true ? { showCaption: true as const } : {}),
					...(img.lazyLoad === false ? { lazyLoad: false as const } : {}),
					...(img.openFullSizeOnClick === true
						? { openFullSizeOnClick: true as const }
						: {}),
					href: img.link || "",
					linkTarget: img.linkTarget || "_self",
					objectFit: element.styles.objectFit,
					objectPosition: element.styles.objectPosition,
					...(typeof img.deliveryVariant === "string" &&
					img.deliveryVariant.trim() &&
					img.deliveryVariant.trim().toLowerCase() !== "auto"
						? { deliveryVariant: img.deliveryVariant.trim() }
						: {}),
					...(typeof img.slotVariantKey === "string" &&
					img.slotVariantKey.trim()
						? {
								slotVariantKey: img.slotVariantKey.trim(),
								...(typeof img.slotVariantRasterPx === "number" &&
								Number.isFinite(img.slotVariantRasterPx) &&
								img.slotVariantRasterPx > 0
									? {
											slotVariantRasterPx: Math.floor(img.slotVariantRasterPx),
										}
									: {}),
								...(typeof img.slotVariantCssPx === "number" &&
								Number.isFinite(img.slotVariantCssPx) &&
								img.slotVariantCssPx > 0
									? {
											slotVariantCssPx: Math.round(img.slotVariantCssPx),
										}
									: {}),
							}
						: {}),
				},
				style: toBuilderStyle(element.styles),
			};
		}
		case "iconify_icon": {
			const ic = element as IconifyIconElement;
			return {
				id: element.id,
				type: "iconify_icon",
				props: {
					iconId: ic.iconId ?? "",
					svgMarkup: ic.svgMarkup ?? "",
					ariaLabel: ic.ariaLabel ?? "",
					href: ic.link || "",
					linkTarget: ic.linkTarget || "_self",
				},
				style: toBuilderStyle(element.styles),
			};
		}
		case "lordicon_icon": {
			const lo = element as LordiconIconElement;
			return {
				id: element.id,
				type: "lordicon_icon",
				props: {
					lordiconSrc: lo.lordiconSrc ?? "",
					trigger: lo.trigger ?? "hover",
					primaryColor: lo.primaryColor ?? "",
					secondaryColor: lo.secondaryColor ?? "",
					ariaLabel: lo.ariaLabel ?? "",
				},
				style: toBuilderStyle(element.styles),
			};
		}
		case "lucide_animated_icon": {
			const la = element as LucideAnimatedIconElement;
			return {
				id: element.id,
				type: "lucide_animated_icon",
				props: {
					mode: la.mode === "local" ? "local" : "library",
					libraryIconId: la.libraryIconId?.trim() || "SparklesIcon",
					localSvgMarkup: la.localSvgMarkup ?? "",
					ariaLabel: la.ariaLabel ?? "",
					primaryColor: la.primaryColor ?? "",
					secondaryColor: la.secondaryColor ?? "",
					strokeWidth: la.strokeWidth?.trim() || "2",
					playOn: la.playOn === "click" ? "click" : "hover",
					animationHostDomId: la.animationHostDomId?.trim() ?? "",
				},
				style: toBuilderStyle(element.styles),
			};
		}
		case "container": {
			const contEl =
				element as import("@multi-tenant-cms/builder/ui/types").ContainerElement;
			const props = containerSemanticProps(contEl);
			if (contEl.contentWidth === "fullViewport") {
				props.__builderContentWidth = "fullViewport";
			}
			if (typeof contEl.scopedCss === "string" && contEl.scopedCss.trim()) {
				props.__builderScopedCss = contEl.scopedCss;
			}
			if (hasContainerBackgroundLayers(contEl.background)) {
				props.__builderContainerBackground = contEl.background;
			}
			// Tab label when this container is a `panel_switcher` child — not reachable
			// from mega-menu/lightbox content (panel_switcher isn't on either preset's
			// palette), kept only so a stray legacy value round-trips instead of vanishing.
			if (typeof contEl.panelLabel === "string" && contEl.panelLabel.trim()) {
				props.__builderPanelLabel = contEl.panelLabel;
			}
			return {
				id: element.id,
				type: "container",
				props,
				children: contEl.children.map(toNarrowBuilderNode),
				style: toBuilderStyle(element.styles),
			};
		}
		case "column": {
			const colEl =
				element as import("@multi-tenant-cms/builder/ui/types").ColumnElement;
			return {
				id: element.id,
				type: "column",
				props: {
					widthFraction:
						typeof colEl.widthFraction === "number" ? colEl.widthFraction : 1,
					...(colEl.settings !== undefined && colEl.settings !== null
						? { __columnSettings: colEl.settings }
						: {}),
					...(colEl.responsiveColumnLayout
						? { __builderResponsiveColumnLayout: colEl.responsiveColumnLayout }
						: {}),
				},
				children: (colEl.children || []).map(toNarrowBuilderNode),
				style: toBuilderStyle(element.styles),
			};
		}
		case "columns": {
			const colsEl =
				element as import("@multi-tenant-cms/builder/ui/types").ColumnsElement;
			const colCount = colsEl.columns ?? colsEl.children?.length ?? 2;
			const columnSettings = colsEl.children.map(
				(col) =>
					col.settings ?? {
						widthType: "auto" as const,
						align: "left" as const,
						alignVertical: "top" as const,
					}
			);
			const columnWidths = columnSettings.map((s) =>
				s.widthType === "percent" && s.widthPercent != null
					? `${s.widthPercent}%`
					: "1fr"
			);
			return {
				id: element.id,
				type: "container",
				props: {
					tag: "section",
					__builderColumns: colCount,
					__builderColumnWidths: columnWidths,
					__builderColumnSettings: columnSettings,
					__builderColumnsAlign: colsEl.alignItems,
					__builderColumnsJustify: colsEl.justifyContent,
					__builderColumnsWrap: colsEl.wrap,
					...(typeof colsEl.wrapColumns === "number"
						? { __builderColumnsWrapColumns: colsEl.wrapColumns }
						: {}),
					__builderColumnsGap: colsEl.gap,
					...(colsEl.responsiveColumnsLayout
						? {
								__builderResponsiveColumnsLayout:
									colsEl.responsiveColumnsLayout,
							}
						: {}),
					...(colsEl.contentWidth === "fullViewport"
						? { __builderContentWidth: "fullViewport" as const }
						: {}),
				} as BuilderColumnsProps,
				children: colsEl.children.map((col) => toNarrowBuilderNode(col)),
				style: toBuilderStyle({
					...element.styles,
					display: "flex",
					flexDirection: "row",
					flexWrap: colsEl.wrap ? "wrap" : "nowrap",
					gap: colsEl.gap || element.styles.gap || "24px",
					alignItems: colsEl.alignItems || "stretch",
					justifyContent: colsEl.justifyContent || "flex-start",
				}),
			};
		}
		case "spacer":
			return {
				id: element.id,
				type: "container",
				props: { tag: "div" },
				children: [],
				style: toBuilderStyle({
					height: element.height,
					minHeight: element.height,
					...element.styles,
				}),
			};
		case "divider":
			return {
				id: element.id,
				type: "container",
				props: { tag: "div" },
				children: [],
				style: toBuilderStyle({
					borderWidth: element.thickness || "1px",
					borderStyle: element.style || "solid",
					borderColor: element.styles.backgroundColor || "#e2e8f0",
					...element.styles,
				}),
			};
		case "navigation_menu": {
			const nav = element as NavigationMenuElement;
			const navProps: BuilderNavigationMenuProps = {
				tag: "div",
				__builderNavigationMenu: true,
				__menuId: nav.menuId,
				__menuName: nav.menuName,
				__menuOrientation: nav.orientation,
				__menuItems: nav.items ?? [],
			};
			const linkColor = nav.linkColor?.trim();
			if (linkColor) {
				navProps.__navLinkColor = linkColor;
			}
			const linkHover = nav.linkHoverColor?.trim();
			if (linkHover) {
				navProps.__navLinkHoverColor = linkHover;
			}
			const linkActive = nav.linkActiveColor?.trim();
			if (linkActive) {
				navProps.__navLinkActiveColor = linkActive;
			}
			return {
				id: element.id,
				type: "container",
				props: navProps,
				children: [],
				style: toBuilderStyle({
					...nav.styles,
					display: nav.styles.display ?? "flex",
					flexDirection:
						nav.styles.flexDirection ??
						(nav.orientation === "vertical" ? "column" : "row"),
					flexWrap:
						nav.orientation === "horizontal"
							? (nav.styles.flexWrap ?? "wrap")
							: nav.styles.flexWrap,
				}),
			};
		}
		case "custom_code": {
			const codeEl = element as CustomCodeElement;
			return {
				id: element.id,
				type: "container",
				props: {
					tag: "div",
					__builderCustomCode: true,
					__codeType: codeEl.codeType,
					__sourceCode: codeEl.sourceCode,
					__cssCode: codeEl.cssCode,
					__jsCode: codeEl.jsCode,
					__compiledCode: codeEl.compiledCode,
					__componentId: codeEl.componentId,
					__compiledAt: codeEl.compiledAt,
					__validationStatus: codeEl.validationStatus,
					__validationMessages: codeEl.validationMessages,
					__dependencies: codeEl.dependencies,
					__componentName: codeEl.componentName,
					__staticHtml: codeEl.staticHtml,
					__parkedCode: codeEl.parkedCode,
				} as BuilderCustomCodeProps,
				children: [],
				style: toBuilderStyle(element.styles),
			};
		}
		case "sortable_list": {
			const s = element as SortableListElement;
			return {
				id: s.id,
				type: "container",
				props: {
					tag: "div",
					__builderSortableList: true,
					__sortableVariant: s.listVariant,
					__sortableMarkerStyle: s.markerStyle,
					__sortableMarkerColor: s.markerColor,
					__sortableMarkerOpacity: s.markerOpacity,
					__sortableItemsJson: JSON.stringify(s.items ?? []),
				} as Record<string, unknown>,
				children: [],
				style: toBuilderStyle(s.styles),
			};
		}
		case "faq": {
			const f = element as FaqElement;
			return {
				id: f.id,
				type: "container",
				props: {
					tag: "div",
					__builderFaq: true,
					__faqItemsJson: JSON.stringify(f.items ?? []),
					__faqExpandMode: f.expandMode === "single" ? "single" : "multiple",
					__faqDefaultOpenFirst: Boolean(f.defaultOpenFirst),
					__faqTriggerIcon: f.triggerIcon ?? "chevron",
					__faqIconPosition: f.iconPosition === "left" ? "left" : "right",
					__faqSearchable: Boolean(f.searchable),
					__faqShowCategoryFilter: Boolean(f.showCategoryFilter),
					__faqItemGap: typeof f.itemGap === "string" ? f.itemGap : "",
					__faqQuestionTag: normalizeHeadingElementTag(f.questionTag ?? "h3"),
					__faqDefaultItemStylesJson: JSON.stringify(f.defaultItemStyles ?? {}),
					__faqDefaultQuestionStylesJson: JSON.stringify(
						f.defaultQuestionStyles ?? {}
					),
					__faqDefaultAnswerStylesJson: JSON.stringify(
						f.defaultAnswerStyles ?? {}
					),
					__faqDefaultChipStylesJson: JSON.stringify(f.defaultChipStyles ?? {}),
					__faqActiveChipStylesJson: JSON.stringify(f.activeChipStyles ?? {}),
					__faqDesignJson: JSON.stringify(f.design ?? { preset: "outlined" }),
				} as Record<string, unknown>,
				children: [],
				style: toBuilderStyle(f.styles),
			};
		}
		case "form_embed": {
			const fe = element as FormEmbedElement;
			return {
				id: fe.id,
				type: "container",
				props: {
					tag: "div",
					__builderFormEmbed: true,
					__formEmbedId: fe.formId,
				} as BuilderFormEmbedProps,
				children: [],
				style: toBuilderStyle(fe.styles),
			};
		}
		default:
			return null;
	}
}

/** An invisible placeholder for a node this converter can't build — see `toNarrowBuilderNode`. */
function emptyFallbackNode(elementId: string): ElementNode {
	return {
		id: elementId,
		type: "container",
		props: { tag: "div" },
		children: [],
	} as ElementNode;
}

/**
 * Converts a live-editor `Element` (the shape mega-menu / lightbox content is
 * persisted in) into the canonical `ElementNode` the public runtime renders — the
 * narrow, synchronous counterpart to `pageBuilderAdapter.ts`'s `toBuilderNode`,
 * restricted to the 17 types those two canvases can ever legitimately contain
 * (enforced going forward by `assertOnlyAllowedElementTypesInBuilder` in the admin
 * editor).
 *
 * An element outside that list — old data predating the palette restriction, or a
 * gap the enforcement missed — renders as an empty, invisible placeholder
 * (same id, no content) rather than throwing: one unexpected node degrades to
 * "silently missing" instead of taking down the whole panel/lightbox. Logged via
 * `console.warn` so it shows up in the region instance's server logs even though
 * there's no dedicated error-reporting sink wired into the public renderer.
 */
export function toNarrowBuilderNode(element: Element): ElementNode {
	const built = buildNarrowBuilderNode(element);
	if (!built) {
		console.warn(
			`[narrowBuilderNodeSerializer] Element type "${element.type}" (id ${element.id}) is not one of the 17 types mega-menus/lightboxes allow — rendering it as empty.`
		);
		return emptyFallbackNode(element.id);
	}
	const base = attachLegacyFlatStyles(element, withDom(element, built));
	return element.responsiveOverrides
		? { ...base, responsiveOverrides: element.responsiveOverrides }
		: base;
}
